import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ResponseDetail, ResponseDiff, ResponseVersion } from '@grc/types';
import { apiFetch } from '../../../lib/api.js';
import {
  Badge,
  Button,
  Dialog,
  Select,
  Spinner,
  formatDateTime,
} from '../../../components/ui.js';
import { Markdown } from '../../../components/Markdown.js';

type VersionSummary = Omit<ResponseVersion, 'content'>;

function DiffView({ diff }: { diff: string }) {
  return (
    <div className="diff-view" role="figure" aria-label="Version diff">
      {diff.split('\n').map((line, i) => {
        const cls = line.startsWith('+')
          ? 'add'
          : line.startsWith('-')
            ? 'del'
            : line.startsWith('@@')
              ? 'hunk'
              : '';
        return (
          <span key={i} className={cls}>
            {line || ' '}
            {'\n'}
          </span>
        );
      })}
    </div>
  );
}

export function VersionHistory({ response }: { response: ResponseDetail }) {
  const versions = useQuery({
    queryKey: ['versions', response.id, response.currentVersion],
    queryFn: () => apiFetch<VersionSummary[]>(`/responses/${response.id}/versions`),
  });

  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const viewed = useQuery({
    queryKey: ['version', response.id, viewVersion],
    queryFn: () => apiFetch<ResponseVersion>(`/responses/${response.id}/versions/${viewVersion}`),
    enabled: viewVersion !== null,
  });

  const [from, setFrom] = useState<number>(Math.max(1, response.currentVersion - 1));
  const [to, setTo] = useState<number>(response.currentVersion);
  const [showDiff, setShowDiff] = useState(false);
  const diff = useQuery({
    queryKey: ['diff', response.id, from, to],
    queryFn: () =>
      apiFetch<ResponseDiff>(`/responses/${response.id}/diff`, { query: { from, to } }),
    enabled: showDiff && from !== to,
  });

  if (versions.isLoading) return <Spinner label="Loading versions" />;
  const items = versions.data ?? [];
  if (items.length === 0) {
    return <p style={{ color: 'var(--color-text-muted)' }}>No versions saved yet.</p>;
  }

  return (
    <div>
      {items.map((v) => (
        <div key={v.id} className="version-item">
          <div>
            <strong>v{v.versionNumber}</strong>{' '}
            {v.aiGenerated && <Badge tone="purple">AI</Badge>}{' '}
            <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
              {v.authorName ?? v.authorId} · {formatDateTime(v.createdAt)}
            </span>
            {v.changeSummary && (
              <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                {v.changeSummary}
              </div>
            )}
          </div>
          <Button size="sm" variant="ghost" onClick={() => setViewVersion(v.versionNumber)}>
            View
          </Button>
        </div>
      ))}

      {items.length > 1 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 'var(--text-xs)' }} htmlFor="diff-from">
              Compare
            </label>
            <Select
              id="diff-from"
              value={from}
              onChange={(e) => setFrom(Number(e.target.value))}
              style={{ width: 90 }}
            >
              {items.map((v) => (
                <option key={v.versionNumber} value={v.versionNumber}>
                  v{v.versionNumber}
                </option>
              ))}
            </Select>
            <span aria-hidden="true">→</span>
            <Select
              aria-label="Compare to version"
              value={to}
              onChange={(e) => setTo(Number(e.target.value))}
              style={{ width: 90 }}
            >
              {items.map((v) => (
                <option key={v.versionNumber} value={v.versionNumber}>
                  v{v.versionNumber}
                </option>
              ))}
            </Select>
            <Button size="sm" variant="secondary" onClick={() => setShowDiff(true)} disabled={from === to}>
              Diff
            </Button>
          </div>
          {showDiff && diff.data && <div style={{ marginTop: 12 }}><DiffView diff={diff.data.unifiedDiff} /></div>}
          {showDiff && diff.isLoading && <Spinner label="Computing diff" />}
        </div>
      )}

      <Dialog
        open={viewVersion !== null}
        onClose={() => setViewVersion(null)}
        title={`Version ${viewVersion ?? ''}`}
      >
        {viewed.isLoading ? (
          <Spinner label="Loading version" />
        ) : viewed.data ? (
          <>
            <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
              {viewed.data.authorName ?? viewed.data.authorId} · {formatDateTime(viewed.data.createdAt)}
              {viewed.data.aiGenerated && (
                <>
                  {' '}
                  <Badge tone="purple">AI generated</Badge>
                </>
              )}
            </p>
            <Markdown markdown={viewed.data.content} />
          </>
        ) : null}
        <div className="dialog-actions">
          <Button onClick={() => setViewVersion(null)}>Close</Button>
        </div>
      </Dialog>
    </div>
  );
}
